import { ValidatorFn } from "@angular/forms";
import { ColumnDef } from "./column-definition";
import { BeyondSupplierInterface, Permission, Company } from "./beyond-supplier-interface";
import { Validators } from '@angular/forms';

/** Validators for fields */
const validators = new Map<string, ValidatorFn[]>([
    ["email", [
        Validators.required,
        Validators.email,
    ]],
    ["firstName", [
        Validators.required,
        Validators.pattern(/^[a-zA-Z\s]+$/),
    ]],
    ["lastName", [
        Validators.required,
        Validators.pattern(/^[a-zA-Z\s]+$/),
    ]],
]);

/**
 * BeyondSupplier represents a beyond supplier user from the backend
 */
export class BeyondSupplier {
    public id: number;
    public email: string;
    public permissions: Permission[];
    public companies: Company[];
    public firstName: string;
    public lastName: string;
    public status: 'terminated' | 'active' | 'pending' | 'suspended';
    public group: string;

    /** Static validators map for access */
    public static validators = validators;

    /** Getters */
    public get fullName(): string {
        return `${this.firstName} ${this.lastName}`;
    }

    public get primaryCompanyId(): number | null {
        return this.companies.length > 0 ? this.companies[0]?.companyId : null;
    }

    public get primaryCompanyType(): 'BORROWER' | 'SUPPLIER' | '' {
        return this.companies.length > 0 ? this.companies[0]?.companyType : "";
    }

    /**
     * Create a new beyond supplier
     * 
     * @param intf The interface to create it from
     */
    public constructor(intf: BeyondSupplierInterface) {
        this.id = intf.id;
        this.email = intf.email;
        this.permissions = intf.permissions || [];
        this.companies = intf.companies || [];
        this.firstName = intf.firstName;
        this.lastName = intf.lastName;
        this.status = intf.status;
        this.group = intf.group;
    }

    /**
     * Copy constructor
     * 
     * @return A new copy of this beyond supplier
     */
    public copyOf(): BeyondSupplier {
        return new BeyondSupplier(this.asInterface());
    }

    /**
     * Convert to interface
     * 
     * @return The interface representation of this beyond supplier
     */
    public asInterface(): BeyondSupplierInterface {
        return {
            id: this.id,
            email: this.email,
            permissions: this.permissions,
            companies: this.companies,
            firstName: this.firstName,
            lastName: this.lastName,
            status: this.status,
            group: this.group,
        };
    }

    /**
     * Compare two beyond suppliers
     * 
     * @param other The other beyond supplier to compare to
     * @return Whether they are equal
     */
    public equals(other: BeyondSupplier): boolean {
        return (
            this.id === other.id &&
            this.email === other.email &&
            this.firstName === other.firstName &&
            this.lastName === other.lastName &&
            this.status === other.status &&
            this.group === other.group &&
            JSON.stringify(this.permissions) === JSON.stringify(other.permissions) &&
            JSON.stringify(this.companies) === JSON.stringify(other.companies)
        );
    }

    /**
     * Get a string representation of the beyond supplier for filtering
     * 
     * @param colDefs The column definitions for the table
     * @return A string representation of the beyond supplier
     */
    public toString(defs: ColumnDef[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /**
     * Get the validator for a field
     * 
     * @param field The field to get the validator for
     * @return The validator for the field
     */
    public static getValidator(field: string): ValidatorFn[] {
        return validators.get(field) || [];
    }

    /**
     * Get the field names for validation
     * 
     * @return The field names
     */
    public static getFieldNames(): string[] {
        return Array.from(validators.keys());
    }
}
